import React, { useState, useEffect } from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { professionalClientDashboardService } from '../../../services/professionalClientDashboardService';

const AnalyseCouts = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('6_mois');

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await professionalClientDashboardService?.getCostAnalytics();
      setAnalyticsData(data);
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError(err?.message || 'Erreur lors du chargement des analyses');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des analyses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <Icon name="AlertCircle" size={24} className="text-red-600" />
          <h3 className="text-lg font-semibold text-red-900">Erreur</h3>
        </div>
        <p className="text-red-700 mb-4">{error}</p>
        <Button onClick={loadAnalytics} variant="outline">
          Réessayer
        </Button>
      </div>
    );
  }

  const { kpis, evolutionCouts, repartitionParType } = analyticsData || {};

  const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Analyse des coûts</h2>
        <Button
          onClick={() => window.print()}
          variant="outline"
          className="flex items-center gap-2"
        >
          <Icon name="Download" size={18} />
          Exporter en PDF
        </Button>
      </div>
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Icon name="DollarSign" size={20} className="text-green-600" />
            </div>
          </div>
          <p className="text-gray-600 text-sm mb-1">Dépenses totales cette année</p>
          <p className="text-2xl font-bold text-gray-900">{kpis?.depensesTotales?.toLocaleString('fr-FR') || 0} €</p>
        </div>

        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Icon name="TrendingUp" size={20} className="text-blue-600" />
            </div>
          </div>
          <p className="text-gray-600 text-sm mb-1">Coût moyen par expédition</p>
          <p className="text-2xl font-bold text-gray-900">{kpis?.coutMoyen?.toLocaleString('fr-FR') || 0} €</p>
        </div>

        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Icon name="Package" size={20} className="text-purple-600" />
            </div>
          </div>
          <p className="text-gray-600 text-sm mb-1">Volume total expédié</p>
          <p className="text-2xl font-bold text-gray-900">{kpis?.volumeTotal || 0}</p>
        </div>

        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Icon name="Award" size={20} className="text-orange-600" />
            </div>
          </div>
          <p className="text-gray-600 text-sm mb-1">Économies grâce au tarif pro</p>
          <p className="text-2xl font-bold text-green-600">{kpis?.economiesRealisees?.toLocaleString('fr-FR') || 0} €</p>
        </div>
      </div>
      {/* Cost Evolution Chart */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Évolution des coûts mensuels</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={evolutionCouts}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="mois" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="coutReel" fill="#10b981" name="Coût réel (tarif pro)" />
            <Bar dataKey="coutStandard" fill="#d1d5db" name="Coût standard" opacity={0.5} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {/* Type Distribution Chart */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition par type d'envoi</h3>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={repartitionParType}
              dataKey="montant"
              nameKey="type"
              cx="50%"
              cy="50%"
              outerRadius={100}
              label={(entry) => `${entry?.type}: ${entry?.pourcentage}%`}
            >
              {repartitionParType?.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default AnalyseCouts;